/**
 * CEILING CORE DEFINITION (2026-08-18, Matt — "many ceilings are framed; some are
 * applied to the underside of the roof structure so they are just a finish layer…
 * it's rare to have the roof completely covered in ceiling"): a ceiling TYPE declares
 * whether it's FINISH-ONLY (the default — a gyp lid hung from whatever structure is
 * above, no core of its own) or FRAMED with its own ceiling joists (Wood / TJI, the
 * floor families' sizes and materials — a joist is a joist). Framed ceilings derive
 * joist framing exactly like floors (bim-geometry floor/framing.ts#buildCeilingFraming,
 * the new Ceiling Framing viewport system); finish-only ceilings derive nothing.
 *
 * INSERT-ON-DECLARE: a finish-only assembly (single gyp layer, coreBoundary {0,0})
 * that declares a joist family gains a NEW structure layer inserted at the core
 * index — the existing finish is never overwritten (unlike walls/floors, whose core
 * layer is always structure by construction). Ceilings stack top→bottom, so the
 * inserted joists land ABOVE the finish, which is physically right.
 */
import type { AssemblyLayer, MaterialId, ParamValue } from './types'
import { FLOOR_CORE_MATERIALS, TJI_JOIST_SIZES, WOOD_JOIST_SIZES } from './floorCore'

export const CEILING_CORE_TYPES = [
  { value: 'none', label: 'Finish Only (no framing)' },
  { value: 'wood', label: 'Wood Joists' },
  { value: 'tji', label: 'TJI Joists' },
] as const
export type CeilingCoreType = (typeof CEILING_CORE_TYPES)[number]['value']

const coreTypeOf = (params: Record<string, ParamValue>): CeilingCoreType => {
  const v = String(params.coreType ?? 'none')
  return v === 'wood' || v === 'tji' ? v : 'none'
}

/** DERIVED core thickness in feet — null for finish-only */
export function ceilingCoreThicknessFt(params: Record<string, ParamValue>): number | null {
  const ct = coreTypeOf(params)
  if (ct === 'none') return null
  const size = ct === 'wood'
    ? WOOD_JOIST_SIZES.find((s) => s.value === String(params.woodJoistSize)) ?? WOOD_JOIST_SIZES[1]
    : TJI_JOIST_SIZES.find((s) => s.value === String(params.tjiJoistSize)) ?? TJI_JOIST_SIZES[1]
  return Math.round((size.actualIn / 12) * 1e4) / 1e4
}

/** '2×8 Wood Joist' / '11⅞″ TJI' — null for finish-only (name from the materials) */
export function ceilingCoreLabel(params: Record<string, ParamValue>): string | null {
  const ct = coreTypeOf(params)
  if (ct === 'none') return null
  if (ct === 'wood') {
    const s = WOOD_JOIST_SIZES.find((x) => x.value === String(params.woodJoistSize)) ?? WOOD_JOIST_SIZES[1]
    return `${s.label} Wood Joist`
  }
  const s = TJI_JOIST_SIZES.find((x) => x.value === String(params.tjiJoistSize)) ?? TJI_JOIST_SIZES[1]
  return `${s.label} TJI`
}

/**
 * Conform (the shared contract), with the ceiling-specific INSERT-ON-DECLARE: when
 * the layer at the core index isn't a structure layer, a new joist layer is inserted
 * AT that index instead of overwriting the finish. Inserting at `core.start` keeps
 * the boundary pointing at the new structure layer, so no boundary patch is needed.
 */
export function conformCeilingCoreLayers(
  params: Record<string, ParamValue>,
  layers: AssemblyLayer[],
  core: { start: number; end: number },
  materialIdByName: (name: string) => string | null,
): AssemblyLayer[] | null {
  const ct = coreTypeOf(params)
  if (ct === 'none' || !layers.length) return null
  const spec = FLOOR_CORE_MATERIALS[ct]!
  const matId = materialIdByName(spec.name)
  const thicknessFt = ceilingCoreThicknessFt(params)!
  const coreIdx = Math.min(Math.max(core.start, 0), layers.length - 1)
  const cur = layers[coreIdx]
  if (cur.function !== 'structure') {
    if (!matId) return null                    // nothing to insert with — save-side conform creates it
    return [
      ...layers.slice(0, coreIdx),
      { function: 'structure', materialId: matId as MaterialId, thicknessFt },
      ...layers.slice(coreIdx),
    ]
  }
  const nextMat = (matId ?? cur.materialId) as MaterialId
  if (cur.materialId === nextMat && Math.abs(cur.thicknessFt - thicknessFt) < 1e-9) return null
  return layers.map((l, i) => (i === coreIdx ? { ...l, materialId: nextMat, thicknessFt } : l))
}

/** Does this ceiling frame? The floor heuristic (resolved core MATERIAL) — a
 *  finish-only ceiling's core is gyp, never woodish, so it derives nothing. */
export function isFramedCeilingCoreMaterial(name: string, category?: string): boolean {
  if (category === 'Wood') return true
  return /\b(wood|joist|tji|timber|lumber)\b/i.test(name)
}
